//================================================================
//  Application: Application Management Console
//================================================================

//  Product Owner: Shaun Brooks
//  Created 30/10/23

//================================================================

// Libraries
import React, { useContext, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';

// Contexts
import { SetAppSize, GetAppSize, GetUser } from './Library/GlobalContexts.js';

//Pages
import Home from './Pages/Home/Home.js';
import Account from './Pages/Mobile/Account.js';
import DigitalCommercialRequestForm from './Pages/DigitalCommercialRequest/DigitalCommercialRequest.js';
import Requests from './Pages/Account/Requests/Requests.js';
import RequestDetails from './Pages/Account/Requests/RequestDetails.js';
import Approvals from './Pages/Account/Approvals/Approvals.js';
import ApprovalDetails from './Pages/Account/Approvals/ApprovalDetails.js';
import Help from './Pages/Help/Help.js';
import NotFound from './Pages/NotFound/NotFound.js';

// Pages - Mobile View
import ApprovalsM from './Pages/Mobile/Approvals/ApprovalsM.js';
import ApprovalDetailsM from './Pages/Mobile/Approvals/ApprovalDetailsM.js';
import RequestsM from './Pages/Mobile/Requests/RequestsM.js';
import Approvers from './Pages/Account/Admin/Approvers/Approvers.js';
import RequestDetailsM from './Pages/Mobile/Requests/RequestDetailsM.js';
import ApproversM from './Pages/Mobile/Approvers/ApproversM.js';

// Components
import Navbar from './Components/Navbar/Navbar';
import Footer from './Components/Footer/Footer.js';
import GlobalToast from './Components/GlobalToast/GlobalToast.js';

// CSS
import './App.css'
import 'bootstrap/dist/css/bootstrap.min.css';

export default function App() {

  //------------------------------------------------------
  //  useContexts
  //------------------------------------------------------

  const getAppSize = useContext(GetAppSize);
  const setAppSize = useContext(SetAppSize);
  const getUser = useContext(GetUser);

  // Check if user is a demand management admin
  const approverAdmin = getUser?.roles?.demandManagementAdmin === true || getUser?.roles?.commercialAdmin === true;

  // Check if user is an IT user
  const itUser = getUser?.roles?.itUser === true;

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  function handleResize(e) {

    setAppSize({
      'width': e.currentTarget.innerWidth,
      'height': e.currentTarget.innerHeight
    });

  };

  //------------------------------------------------------
  //  useEffect
  //------------------------------------------------------

  // onLoad
  // 1. Setup an event listener to get width and height of app
  useEffect(() => {

    setAppSize({
      'width': window.innerWidth,
      'height': window.innerHeight
    });

    window.addEventListener('load', handleResize)
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('load', handleResize)
      window.removeEventListener('resize', handleResize)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //------------------------------------------------------
  //  Return HTML
  //------------------------------------------------------

  return (
    <div className='App-Container'>

      {/* Nav */}
      <div className='App-Container-Nav'>
        <Navbar></Navbar>
      </div>

      {/* Body > Contains all pages in the app  */}
      <div className='App-Container-Body'>

        <GlobalToast></GlobalToast>

        <Routes>

          <Route path='/' element={<Home />} />
          {
            itUser && <Route path='/digitalcommercialrequest' element={<DigitalCommercialRequestForm />} />
          }
          <Route path='/help' element={<Help />} />
          <Route path='/requests' element={
            getAppSize.width > 768 ?
              <Requests />
              :
              <RequestsM />
          } />
          <Route path='/requests/:id' element={
            getAppSize.width > 768 ?
              <RequestDetails />
              :
              <RequestDetailsM />
          } />
          <Route path='/approvals' element={
            getAppSize.width > 768 ?
              <Approvals />
              :
              <ApprovalsM />
          } />

          <Route path='/approvals/:id' element={
            getAppSize.width > 768 ?
              <ApprovalDetails />
              :
              <ApprovalDetailsM />
          } />
          <Route path='/account' element={<Account />} />

          {
            approverAdmin && <Route path='/admin/approvers' element={
            getAppSize.width > 768 ?
              <Approvers />
              :
              <ApproversM />} />
          }

        <Route path='*' element={<NotFound />}></Route>

        </Routes>

        <Footer></Footer>

      </div>

    </div>
  )

  //------------------------------------------------------
}